/**
 * Accessibility Helper Functions
 * Utilities for improving web accessibility compliance
 */

/**
 * Trap focus within a container (useful for modals)
 */
export const trapFocus = (element) => {
  const focusableElements = element.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];
  
  const handleTabKey = (e) => {
    if (e.key !== 'Tab') return;
    
    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  };
  
  element.addEventListener('keydown', handleTabKey);
  
  return () => {
    element.removeEventListener('keydown', handleTabKey);
  };
};

/**
 * Announce message to screen readers using ARIA live region
 */
export const announceToScreenReader = (message, priority = 'polite') => {
  const announcement = document.createElement('div');
  announcement.setAttribute('role', 'status');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;
  
  document.body.appendChild(announcement);
  
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

/**
 * Check if user prefers reduced motion
 */
export const prefersReducedMotion = () => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Generate unique ID for form elements (for accessibility)
 */
let idCounter = 0;
export const generateA11yId = (prefix = 'a11y') => {
  idCounter += 1;
  return `${prefix}-${idCounter}-${Date.now()}`;
};

/**
 * Get contrast ratio between two colors (simplified)
 */
export const getContrastRatio = (color1, color2) => {
  // Simplified contrast calculation
  // For production, use a proper WCAG contrast calculator
  const getLuminance = (r, g, b) => {
    const [rs, gs, bs] = [r, g, b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  };
  
  const l1 = getLuminance(color1.r, color1.g, color1.b);
  const l2 = getLuminance(color2.r, color2.g, color2.b);
  
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Check if element is visible to screen readers
 */
export const isVisibleToScreenReader = (element) => {
  if (!element) return false;
  
  const style = window.getComputedStyle(element);
  return !(
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    element.getAttribute('aria-hidden') === 'true' ||
    element.hasAttribute('hidden')
  );
};

/**
 * Move focus to element with proper announcement
 */
export const moveFocus = (element, announce = true) => {
  if (!element) return;
  
  element.focus();
  
  if (announce) {
    const label = element.getAttribute('aria-label') || 
                  element.getAttribute('title') || 
                  element.textContent;
    if (label) {
      announceToScreenReader(`התמקדות עברה ל: ${label}`);
    }
  }
};

/**
 * Restore focus to previously focused element
 */
export const createFocusRestore = () => {
  const previouslyFocused = document.activeElement;
  
  return () => {
    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      previouslyFocused.focus();
    }
  };
};

/**
 * Text-to-Speech functionality using Web Speech API
 */
export const textToSpeech = {
  // Check if speech synthesis is supported
  isSupported: () => {
    return 'speechSynthesis' in window;
  },

  // Speak text
  speak: (text, options = {}) => {
    if (!textToSpeech.isSupported()) {
      console.warn('Text-to-speech is not supported in this browser');
      return;
    }

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    
    // Configure options
    utterance.lang = options.lang || 'he-IL'; // Hebrew by default
    utterance.rate = options.rate || 1.0; // Speed (0.1 to 10)
    utterance.pitch = options.pitch || 1.0; // Pitch (0 to 2)
    utterance.volume = options.volume || 1.0; // Volume (0 to 1)

    // Event handlers
    if (options.onStart) utterance.onstart = options.onStart;
    if (options.onEnd) utterance.onend = options.onEnd;
    if (options.onError) utterance.onerror = options.onError;

    window.speechSynthesis.speak(utterance);
  },

  // Stop speaking
  stop: () => {
    if (textToSpeech.isSupported()) {
      window.speechSynthesis.cancel();
    }
  },

  // Pause speaking
  pause: () => {
    if (textToSpeech.isSupported()) {
      window.speechSynthesis.pause();
    }
  },

  // Resume speaking
  resume: () => {
    if (textToSpeech.isSupported()) {
      window.speechSynthesis.resume();
    }
  },

  // Check if currently speaking
  isSpeaking: () => {
    return textToSpeech.isSupported() && window.speechSynthesis.speaking;
  },

  // Get available voices
  getVoices: () => {
    if (!textToSpeech.isSupported()) return [];
    return window.speechSynthesis.getVoices();
  },

  // Get Hebrew voices
  getHebrewVoices: () => {
    return textToSpeech.getVoices().filter(voice => 
      voice.lang.startsWith('he') || voice.lang.startsWith('iw')
    );
  }
};

